import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { JSX } from "react";
import { useResetAtom } from "jotai/utils";

import IllustrationConfetti from "@repo/icons/colored/IllustrationConfetti";
import ArrowRight from "@repo/icons/default/ArrowRight";
import Chart from "@repo/icons/default/Chart";
import Chat from "@repo/icons/default/Chat";
import CheckCircle from "@repo/icons/default/CheckCircle";
import type { SVGIconProps } from "@repo/icons/interface";

import { Button } from "@/components/ui/button/button";
import { ProgressRoundedLayout } from "@/components/ui/layout/progress-rounded-layout";
import { consultingAtom } from "@/lib/atoms/consulting-atom";

interface ConsultingBenefit {
  Icon: (props: SVGIconProps) => JSX.Element;
  text: string;
}

const CONSULTORIA_BENEFITS: ConsultingBenefit[] = [
  {
    Icon: Chat,
    text: "Sessões individuais e periódicas com consultor especialista",
  },
  {
    Icon: CheckCircle,
    text: "Avaliação de dores, metas e objetivos profissionais",
  },
  {
    Icon: Chart,
    text: "Plano de ação personalizado para o seu consultório",
  },
];

const ConsultingIntroPage = (): JSX.Element => {
  const navigate = useNavigate();
  const resetConsulting = useResetAtom(consultingAtom);

  const handleStart = (): void => {
    resetConsulting();
    void navigate({ to: "/onboarding/consulting/channels" });
  };

  return (
    <ProgressRoundedLayout progress={1 / 5}>
      <div className="flex h-full flex-col justify-between gap-6">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <IllustrationConfetti size={80} aria-hidden="true" />
            <h1 className="text-2xl font-medium leading-[1.32] text-neutral-700">
              Você ganhou uma consultoria na unidade selecionada
            </h1>
            <p className="text-base font-normal leading-[1.32] text-neutral-500">
              Responda as perguntas a seguir para concluir o agendamento da consultoria
            </p>
          </div>

          <div className="flex flex-col gap-4 rounded-xl bg-neutral-50 p-4">
            {CONSULTORIA_BENEFITS.map(({ Icon, text }) => (
              <div key={text} className="flex items-start gap-3">
                <Icon
                  size={24}
                  className="fill-primary-400 mt-0.5 shrink-0"
                  aria-hidden="true"
                />
                <span className="text-sm leading-[1.45] text-neutral-700">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="sticky bottom-0 flex w-full flex-col bg-white pb-2">
          <Button fullWidth onClick={handleStart}>
            <div className="flex flex-row items-center gap-2">
              Agendar consultoria
              <ArrowRight size={24} />
            </div>
          </Button>
        </div>
      </div>
    </ProgressRoundedLayout>
  );
};

export const Route = createFileRoute("/onboarding/consulting/intro")({
  component: ConsultingIntroPage,
});
